import Casing from '@/helpers/casing'

import METRIC_CONSTANTS from './METRIC_CONSTANTS'
import METRIC_KEYS from './METRIC_KEYS'

import Metric from './metric'
import ImprovementBasics from './improvement-basics'

/**
 * Defined structure of an improvement.
 * @export Improvement
 * @class Improvement
 */
export default class Improvement extends ImprovementBasics {

    /**
     * Creates an instance of an Improvement.
     * @param {ImprovementAsJson|ImprovementBasicsAsJson|ImprovementSourceData|Object} data Source data - From API or state.
     * @param {Array<Metric>} [baseMetrics=[]] Optional base metrics from the property.
     * @param {Array<Metric>} [projectedMetrics=[]] Optional projected metrics from the property.
     * @param {Array<ImprovementBasics>} [alternatives=[]] Optional alternatives data that will apply ONLY if not in the core data.
     */
    constructor(data, { baseMetrics = [], projectedMetrics = [], alternatives = [] } = { baseMetrics: [], projectedMetrics: [], alternatives: [] }) {
        super(data)

        /**
         * Here we need to be able to reload the 'alternatives' property from existing object data but must
         * avoid loading integer only 'alternatives' property data from the API where we use the 'alternatives'
         * parameter on the constructor instead.
         * @param {Object} dataToUse Source data - From API or state.
         * @param {Array<ImprovementBasics>} alts Potentially overriding alternatives passed to the constructor.
         * @returns {Array<ImprovementBasics>}
         */
        const buildAlternatives = function(dataToUse, alts) {
            let alternativesWhichAreObjects = dataToUse.alternatives ? dataToUse.alternatives.filter(item => isNaN(item)) : []
            if (alternativesWhichAreObjects.length < 1) alternativesWhichAreObjects = null
            return (dataToUse.alternativeImprovements || alternativesWhichAreObjects || alts || [])
                .map(item => isNaN(item) ? ImprovementBasics.ensure(item) : null)
                .filter(item => item != null)
        }

        /**
         * Calculates the efficiency data to inform the value of different improvements.
         * @param {Array<Metric>} metricsSrc The array of Metrics objects to base the calculations on.
         * @param {number} cost The cost of the improvement.
         * @returns {ImprovementEfficiencies}
         */
        const calculateEfficiencies = function(metricsSrc, cost) {
            const co2Metric = metricsSrc.find(m => m.key === METRIC_KEYS.carbonSaving)
            const fuelBillMetric = metricsSrc.find(m => m.key === METRIC_KEYS.fuelBill)
            const sapMetric = metricsSrc.find(m => m.key === METRIC_KEYS.sapScore)

            const co2Saving = co2Metric ? co2Metric.saving.value : null
            const fuelBillSavingPerYear = fuelBillMetric ? fuelBillMetric.saving.value : null
            const sapImprovement = sapMetric ? sapMetric.saving.value : 0

            const costPerSapScoreImprovement = sapImprovement > 0
                ? Math.round(cost / sapImprovement * 100) / 100
                : null

            let payback = fuelBillSavingPerYear
                ? Math.round(cost / fuelBillSavingPerYear * 10) / 10
                : null
            if (payback < 0) payback = null

            const co2PerPound = co2Saving ? Math.round(co2Saving / cost * METRIC_CONSTANTS.co2EfficiencyScalingFactor) / METRIC_CONSTANTS.co2EfficiencyScalingFactor : 0
            const co2 = co2Saving ? Math.round(co2Saving * METRIC_CONSTANTS.co2EfficiencyScalingFactor) / METRIC_CONSTANTS.co2EfficiencyScalingFactor : 0

            const rateBaseCo2 = co2 || METRIC_CONSTANTS.defaultCo2EfficiencyFactor
            const rateBasePayback = payback || METRIC_CONSTANTS.defaultPaybackFactor
            const effectivenessRating = !payback && !co2 ? 0 : Math.round(rateBaseCo2 / rateBasePayback * METRIC_CONSTANTS.co2EfficiencyScalingFactor) / 10

            return {
                co2,
                co2PerPound,
                costPerSapScoreImprovement,
                effectivenessRating,
                payback,
                sapImprovement,
            }
        }

        /**
         * Parse the metrics from provided data.
         * @param {Array} base Base results.
         * @param {Array} projections Projected results after implementation.
         * @returns {?{metrics: Array<Metric>, savings: Object}}
         */
        const parseMetrics = function(base, projections) {
            if (base.length) {
                const savings = {}
                const metrics = base.map(el => {
                    // projected = projections value if present, else data projected property, else base results value.
                    const projection = projections.find(prj => prj.key === el.key)
                        || { key: el.key, value: el.projected !== undefined ? el.projected : el.value }
                    return Metric.fromKey(el.key,
                        el.value !== undefined ? el.value : el.current,
                        projection.value !== undefined ? projection.value : projection.projected)
                })

                metrics.forEach(m => savings[Casing.camelCase(m.name)] = m.saving.display)
                return { metrics, savings }
            }
            return null
        }

        /** @type {number} **/
        this._priceWithPence = Math.round(data.price * 100) / 100

        /** @type {string} **/
        this.segmentKey = data.segmentKey || 'other'

        /** @type {boolean} **/
        this.accepted = data.accepted || false

        /** @type {boolean} **/
        this.recommended = data.recommended || false

        /** @type {Array<ImprovementBasics>} **/
        this.alternatives = buildAlternatives(data, alternatives)

        const srcBaseMetrics = baseMetrics.length
            ? baseMetrics
            : data.metrics && data.metrics.length
                ? data.metrics : []

        const srcProjectedMetrics = projectedMetrics.length
            ? projectedMetrics
            : srcBaseMetrics.length
                ? srcBaseMetrics : []

        const parsedMetrics = parseMetrics(srcBaseMetrics, srcProjectedMetrics)
        if (parsedMetrics) {
            /** @type {Array<Metric>} **/
            this.metrics = parsedMetrics.metrics

            /** @type {Object} **/
            this.savings = parsedMetrics.savings
        }

        /** @type {ImprovementEfficiencies} **/
        this.efficiencies = calculateEfficiencies(this.metrics ? this.metrics : [], this._priceWithPence)
    }

    /**
     * Determines if the improvement has a selected alternative.
     * @param {Array<ImprovementBasics>} improvements
     * @returns {boolean}
     */
    hasSelectedAlternativeIn(improvements) {
        const selectedAlternatives = this.alternatives.map(alt => improvements.find(imp => imp && imp.id === alt.id))
            .filter(imp => imp && imp.accepted)

        return !!(selectedAlternatives && selectedAlternatives.length)
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns ImprovementAsJson
     */
    toStoreJson() {
        const coreJson = {
            id: this.id,
            title: this.title,
            price: this.price,
            description: this.description,
            imageUrl: this.imageUrl,
            hasImage: this.hasImage,
            accepted: this.accepted,
            recommended: this.recommended,
            segmentKey: this.segmentKey,
            details: this.details,
            alternativeImprovements: this.alternatives
                .map(item => item.toStoreJson()),
        }

        if (this.metrics) coreJson.metrics = this.metrics.map(m => m.toStoreJson())
        return coreJson
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * This is not appropriate to use if alts or savings are needed to merge in.
     * @static
     * @param {ImprovementAsJson|ImprovementBasicsAsJson} data Source data - From API or state.
     * @returns {Improvement} Either the data is passed back if already of this type or the result of the constructor is.
     */
    static ensure(data) {
        return data && data instanceof Improvement ? data : new Improvement(data)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?ImprovementAsJson|?ImprovementBasicsAsJson} [data=null] The stored data.
     * @returns Improvement
     */
    static fromStoreJson(data = null) {
        return data ? new Improvement(data) : null
    }
}
